import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCategoryAnalysis } from './context/CategoryAnalysisContext';
import './style/dataTable.scss';
import PlatformIcon from '../../components/Common/PlatformIcon';
import { TABLE_COLUMNS, COLORS } from '../CategoryAnalysis/constants';
import { useSearchProgress } from '../../hooks/useSearchProgress';
import SearchProgressModal from '../Common/Modal/SearchProgressModal';
import { Info } from 'lucide-react';
import { useAnalytics, ANALYTICS_EVENTS } from '../../analytics';

const DataTable = () => {
  const {
    tableData,
    tableDataLoading,
    tableDataError,
    lastUpdated,
    isExplorerUser,
    isPlanLoading,
    openUpgradeModal,
    activeKeyword,
    setActiveKeyword,
    setTableDataLoading,
    fetchTableData,
  } = useCategoryAnalysis();
  const LOCK_PLACEHOLDER = "%UPGRADE_KEYQ%"
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const [expandedRows, setExpandedRows] = useState(new Set());

  const { progress, showProgressModal, cancel: cancelSearchProgress } = useSearchProgress({
    loading: tableDataLoading,
  });

  const handleCancelSearch = () => {
    cancelSearchProgress();
    setTableDataLoading(false);
    setActiveKeyword('');
    try { sessionStorage.removeItem('categoryAnalysisLastSearch'); } catch (e) { /* ignore */ }
  };


  const getRelativeTime = (d) => {
    if (!d) return '';
    try {
      const now = new Date();
      const then = new Date(d);
      const diffMs = now - then;
      const diffSec = Math.round(diffMs / 1000);
      if (diffSec < 5) return 'just now';
      if (diffSec < 60) return `${diffSec} sec${diffSec === 1 ? '' : 's'} ago`;
      const diffMin = Math.round(diffSec / 60);
      if (diffMin < 60) return `${diffMin} min${diffMin === 1 ? '' : 's'} ago`;
      const diffHour = Math.round(diffMin / 60);
      if (diffHour < 24) return `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`;
      const diffDay = Math.round(diffHour / 24);
      if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
      // Fallback to localized date for older timestamps
      return new Date(d).toLocaleString();
    } catch (e) {
      return new Date(d).toLocaleString();
    }
  };

  // Function to format date to MM-DD-YY format
  const formatDate = (dateString) => {
    // return empty string when date missing/invalid so table cell stays blank instead of showing 'N/A'
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';

      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2); // Get last 2 digits of year

      return `${month}-${day}-${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Normalize genre/subcategory to lowercase, comma-separated without spaces
  const genreToSubcat = (genre) => {
    if (!genre) return '';
    if (Array.isArray(genre)) {
      return genre.map(g => String(g || '').trim().toLowerCase()).join(',');
    }
    if (typeof genre === 'string') {
      return genre.split(',').map(g => g.trim().toLowerCase()).join(',');
    }
    return String(genre);
  };

  // Skeleton for table rows
  const renderSkeleton = () => (
    <tbody>
      {Array.from({ length: 5 }).map((_, index) => (
        <tr key={index}>
          <td><div className="skeleton-cell"></div></td>
          <td>
            <div className="game-info">
              <div className="skeleton-image"></div>
              <div className="skeleton-text"></div>
            </div>
          </td>
          <td><div className="skeleton-cell"></div></td>
          <td><div className="skeleton-cell"></div></td>
          <td><div className="skeleton-cell"></div></td>
          <td><div className="skeleton-cell"></div></td>
          <td><div className="skeleton-cell"></div></td>
          <td><div className="skeleton-cell"></div></td>
          <td><div className="skeleton-cell"></div></td>
          <td><div className="skeleton-cell"></div></td>
        </tr>
      ))}
    </tbody>
  );

  // Error state
  if (tableDataError) {
    return (
      <div className="data-table-container">
        <div className="table-error">
          <p>Failed to load data. Please try again later.</p>
        </div>
      </div>
    );
  }

  // If there is no active search keyword, don't show stale or "no results" table;
  // just render an empty state.
  if (!activeKeyword) {
    return (
      <div className="data-table-container">
        <div style={{ padding: 40, textAlign: 'center', color: COLORS.textMuted, fontSize: 16 }}>
          Start by searching a category to see results.
        </div>
      </div>
    );
  }

  // If not loading and no data, show a simple 'No data available' message (no empty table)
  if (!tableDataLoading && Array.isArray(tableData) && tableData.length === 0) {
    return (
      <div className="data-table-container">
        <div style={{ padding: 40, textAlign: 'center', color: COLORS.textMuted, fontSize: 16 }}>
          No results found for the selected platform.
        </div>
      </div>
    );
  }

  return (
    <div className="data-table-container">
      {/* <SearchProgressModal show={showProgressModal} onHide={() => setShowProgressModal(false)} progress={progress} /> */}
      <SearchProgressModal
        show={showProgressModal}
        progress={progress}
        onCancel={handleCancelSearch}
        hasResults={Array.isArray(tableData) && tableData.length > 0}
      />

      <table className="data-table">
        <thead>
          <tr>
            <th>Sno</th>
            <th>Game</th>
            <th>Platform</th>
            <th>Category</th>
            <th>Sub Category</th>
            <th>Price</th>
            <th>Ratings</th>
            <th>Downloads</th>
            <th>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span>Revenue</span>
                <span
                  title="Add-ons revenue included in total"
                  style={{ display: 'inline-flex', alignItems: 'center' }}
                >
                  <Info size={14} style={{ opacity: 0.8, cursor: 'default' }} />
                </span>
              </span>
            </th>
            <th>Release On</th>
          </tr>
        </thead>
        {tableDataLoading ? (
          renderSkeleton()
        ) : (
          <tbody>
            {tableData.length > 0 ? (
              tableData.map((row, index) => {
                try {
                  // Debug: Log the row data to see its structure
                  // derive platform string from multiple potential fields in API response
                  const platformRaw = row.platform || row.platforms || row.game?.platform || row.game?.platform_name || '';
                  const platform = String(platformRaw).toLowerCase();
                  const platformUrl = row.platform_url || row.platformUrl || row.game?.platform_url || row.game?.platformUrl || '';
                  const openPlatformLink = (e) => {
                    // avoid triggering row navigation or default anchor navigation
                    if (e) {
                      e.stopPropagation();
                      e.preventDefault();
                    }
                    const name = row.game?.name || row.name || '';
                    if (platformUrl && platformUrl !== '#') {
                      window.open(platformUrl, '_blank', 'noopener');
                      return;
                    }
                    // If platform is Meta/Oculus and we have an item id, construct direct Meta experiences URL
                    try {
                      const metaItemId = row.game?.meta_item_id || row.game?.metaItemId || row.game?.item_id || row.game?.itemId || row.meta_item_id || row.metaItemId || row.item_id || row.itemId || row.game?.oculus_id || row.game?.oculusId;
                      if (!platformUrl && (platform.includes('meta') || platform.includes('oculus')) && metaItemId) {
                        // derive slug from provided slug or from name
                        const rawSlug = row.game?.slug || row.game?.slug_name || row.game?.slugName || row.game?.name || name || '';
                        const slug = String(rawSlug).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                        const metaUrl = `https://www.meta.com/experiences/${slug}/${metaItemId}`;
                        window.open(metaUrl, '_blank', 'noopener');
                        return;
                      }
                      // Steam: try steam_appid / steamAppId / steam_id
                      const steamAppId = row.game?.steam_appid || row.steam_appid || row.game?.steamAppId || row.steamAppId || row.game?.steam?.appid || row.steam?.appid;
                      if (!platformUrl && platform.includes('steam') && steamAppId) {
                        const steamUrl = `https://store.steampowered.com/app/${steamAppId}/`;
                        window.open(steamUrl, '_blank', 'noopener');
                        return;
                      }
                      // Apple Vision: try apple_item_id / appleItemId / apple_id
                      const appleId = row.game?.apple_item_id || row.apple_item_id || row.game?.appleItemId || row.appleItemId || row.game?.apple_id || row.apple_id;
                      if (!platformUrl && platform.includes('apple') && appleId) {
                        const appleUrl = `https://apps.apple.com/app/id${appleId}`;
                        window.open(appleUrl, '_blank', 'noopener');
                        return;
                      }
                    } catch (e) {
                      // continue to fallback search if anything goes wrong
                      console.warn('Error constructing Meta store URL', e);
                    }
                    // fallback: do a site-specific Google search for the game's store page
                    let domain = '';
                    if (platform.includes('meta') || platform.includes('oculus')) domain = 'oculus.com';
                    else if (platform.includes('steam')) domain = 'store.steampowered.com';
                    else if (platform.includes('apple')) domain = 'apps.apple.com';
                    else domain = '';
                    const q = domain ? `${name} site:${domain}` : name;
                    const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(q)}`;
                    window.open(searchUrl, '_blank', 'noopener');
                  };
                  const goToDashboard = (e) => {
                    // Prevent row click from triggering when clicking links/buttons inside
                    if (e && e.defaultPrevented) return;
                    const id = row.game?.item_id || row.item_id || row.game?.itemId || row.itemId || row.id;
                    if (!id) return;
                    const appName = row.game?.name || row.name || '';
                    trackEvent(ANALYTICS_EVENTS.FEATURE.CATEGORY_EXPLORER_APP_SELECTED, {
                      app_id: id,
                      app_name: appName,
                      genre: activeKeyword,
                      platform: platformRaw || undefined,
                    });
                    navigate(`/xr/app/${appName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}?id=${id}`, {
                      state: {
                        app_id: id,
                        app_name: appName,
                        source_surface: "genre_explorer",
                        search_keyword: activeKeyword,
                      }
                    });
                  };

                  const gameName = row.game?.name || row.name || 'Unknown Game';
                  const truncatedName = gameName.length > 10 ? gameName.substring(0, 10) + '...' : gameName;

                  return (
                    <tr key={row.id || index}>
                      <td>{index + 1}</td>
                      <td>
                        <div className="game-info">
                          <img
                            src={row.game?.image_url || row.image_url || 'https://via.placeholder.com/50'}
                            alt={gameName}
                            className="game-image-clickable"
                            role="button"
                            tabIndex={0}
                            onClick={goToDashboard}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToDashboard(e); } }}
                            aria-label={`Open dashboard for ${gameName}`}
                          />
                          <span title={gameName}>{gameName}</span>
                        </div>
                      </td>
                      <td>
                        <div className="platform-cell">
                          {(() => {
                            if (!platform) return null;
                            // Use dynamic PlatformIcon component for all platforms
                            return (
                              <a href={platformUrl || '#'} target="_blank" rel="noopener noreferrer" title={platform} onClick={openPlatformLink}>
                                <PlatformIcon platform={platform} />
                              </a>
                            );
                          })()}
                        </div>
                      </td>
                      <td>{row.category ?? row.game?.category ?? ''}</td>
                      <td>
                        {(() => {
                          // Prefer explicit genre field when available, otherwise fall back to subCategory/sub_category
                          const genreData = row.genre ?? row.subCategory ?? row.sub_category ?? row.game?.genre;
                          if (!genreData) return '';

                          // Parse genres into array
                          let genres = [];
                          if (Array.isArray(genreData)) {
                            genres = genreData.map(g => String(g || '').trim()).filter(g => g);
                          } else if (typeof genreData === 'string') {
                            genres = genreData.split(',').map(g => g.trim()).filter(g => g);
                          } else {
                            genres = [String(genreData).trim()].filter(g => g);
                          }

                          if (genres.length === 0) return '';
                          if (genres.length === 1) return genres[0];

                          const rowKey = row.id || index;
                          const isExpanded = expandedRows.has(rowKey);

                          if (isExpanded) {
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {genres.map((genre, idx) => (
                                  <span key={idx} style={{ display: 'block', fontSize: 13 }}>{genre}</span>
                                ))}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedRows(prev => {
                                      const newSet = new Set(prev);
                                      newSet.delete(rowKey);
                                      return newSet;
                                    });
                                  }}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#F6255A',
                                    cursor: 'pointer',
                                    fontSize: 13,
                                    padding: '4px 0',
                                    textAlign: 'left',
                                    marginTop: 4
                                  }}
                                >
                                  Show less
                                </button>
                              </div>
                            );
                          } else {
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <span style={{ fontSize: 13 }}>{genres[0]}</span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedRows(prev => {
                                      const newSet = new Set(prev);
                                      newSet.add(rowKey);
                                      return newSet;
                                    });
                                  }}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#F6255A',
                                    cursor: 'pointer',
                                    fontSize: 13,
                                    padding: '4px 0',
                                    textAlign: 'left'
                                  }}
                                >
                                  +{genres.length - 1} more
                                </button>
                              </div>
                            );
                          }
                        })()}
                      </td>
                      <td>
                        {(() => {
                          // Handle price object structure
                          const priceObj = row.price ?? row.game?.price;
                          if (priceObj && typeof priceObj === 'object') {
                            if (priceObj.amount !== undefined && priceObj.amount !== null && priceObj.currency) {
                              return `${priceObj.currency} ${priceObj.amount}`;
                            } else if (priceObj.amount !== undefined && priceObj.amount !== null) {
                              return priceObj.amount;
                            }
                          } else if (priceObj !== null && priceObj !== undefined) {
                            return priceObj;
                          }
                          return '';
                        })()}
                      </td>
                      <td>
                        <div className="rating">
                          <span>
                            {(() => {
                              // Handle different rating structures
                              const ratingObj = row.rating ?? row.game?.rating ?? row.ratings;
                              if (ratingObj && typeof ratingObj === 'object') {
                                // If rating is an object with avg_rating or average property
                                if (ratingObj.avg_rating !== undefined && ratingObj.avg_rating !== null) {
                                  return typeof ratingObj.avg_rating === 'number' ? ratingObj.avg_rating.toFixed(1) : ratingObj.avg_rating;
                                } else if (ratingObj.average !== undefined && ratingObj.average !== null) {
                                  return typeof ratingObj.average === 'number' ? ratingObj.average.toFixed(1) : ratingObj.average;
                                } else if (ratingObj.total_rating !== undefined && ratingObj.total_rating !== null) {
                                  return ratingObj.total_rating;
                                }
                              } else if (ratingObj !== null && ratingObj !== undefined && typeof ratingObj !== 'object') {
                                return ratingObj;
                              }
                              return '';
                            })()}
                          </span>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 1L10.163 5.279L15 6.028L11.5 9.389L12.326 14L8 11.8L3.674 14L4.5 9.389L1 6.028L5.837 5.279L8 1Z" fill="#FFB800" />
                          </svg>
                        </div>
                      </td>
                      <td>
                        {
                          (() => {
                            // Lock only when API returns the placeholder for this row's downloads
                            const rawDownloads = row.downloads ?? row?.stats?.downloads ?? row.game?.downloads ?? row.game?.stats?.downloads
                            const downloadsLocked = (rawDownloads === LOCK_PLACEHOLDER)
                            const locked = downloadsLocked && !isPlanLoading

                            let displayVal = '0'
                            if (downloadsLocked) {
                              displayVal = '0 M'
                            } else {
                              const downloadsNumeric = typeof rawDownloads === 'number' ? rawDownloads : (typeof rawDownloads === 'string' ? Number(String(rawDownloads).replace(/[,\s]/g, '')) : NaN)
                              if (isFinite(downloadsNumeric)) {
                                if (downloadsNumeric >= 1000000000) {
                                  displayVal = `${(downloadsNumeric / 1000000000).toFixed(1)} B`
                                } else if (downloadsNumeric >= 1000000) {
                                  displayVal = `${(downloadsNumeric / 1000000).toFixed(1)} M`
                                } else if (downloadsNumeric >= 1000) {
                                  displayVal = `${(downloadsNumeric / 1000).toFixed(1)} K`
                                } else {
                                  displayVal = `${Math.round(downloadsNumeric)}`
                                }
                              } else {
                                displayVal = rawDownloads !== null && rawDownloads !== undefined ? String(rawDownloads) : '0'
                              }
                            }

                            return (
                              <div
                                className="stat-text"
                                {...(locked ? { role: 'button', tabIndex: 0 } : {})}
                                onClick={(e) => { if (locked) { e.stopPropagation(); openUpgradeModal && openUpgradeModal(); } }}
                                onKeyDown={(e) => { if (locked && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); e.stopPropagation(); openUpgradeModal && openUpgradeModal(); } }}
                              >
                                {locked ? (
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <span
                                      style={{
                                        filter: 'blur(4px)',
                                        WebkitFilter: 'blur(4px)',
                                        opacity: 0.6,
                                        userSelect: 'none',
                                        pointerEvents: 'none'
                                      }}
                                    >
                                      {displayVal}
                                    </span>
                                    <i
                                      className="fas fa-lock"
                                      aria-hidden="true"
                                      style={{
                                        fontSize: 12,
                                        color: '#fff',
                                        cursor: 'pointer'
                                      }}
                                    />
                                  </span>
                                ) : (
                                  <div className="stat-text-inner">
                                    {displayVal}
                                  </div>
                                )}
                              </div>
                            );
                          })()
                        }
                      </td>
                      <td>
                        {
                          (() => {
                            const rawRevenue = row.revenue ?? row?.stats?.revenue ?? row.game?.revenue ?? row.game?.stats?.revenue
                            const revenueLocked = (rawRevenue === LOCK_PLACEHOLDER)
                            const locked = revenueLocked && !isPlanLoading

                            // Check if revenue data actually exists (not null, undefined, or empty string)
                            const revenueExists = rawRevenue !== null && rawRevenue !== undefined && rawRevenue !== '' && rawRevenue !== LOCK_PLACEHOLDER
                            const revenueNumeric = typeof rawRevenue === 'number' ? rawRevenue : (typeof rawRevenue === 'string' && rawRevenue !== LOCK_PLACEHOLDER ? Number(String(rawRevenue).replace(/[,\s]/g, '')) : NaN)

                            let formatted = ''
                            if (revenueLocked) {
                              formatted = `$ 0 M`
                            } else if (!revenueExists || (!isFinite(revenueNumeric) && rawRevenue !== 0)) {
                              // Data is missing or invalid (but 0 is valid)
                              formatted = `$0.0`
                            } else if (isFinite(revenueNumeric)) {
                              // Data exists, format it appropriately
                              if (revenueNumeric >= 1000000000) {
                                formatted = `$ ${(revenueNumeric / 1000000000).toFixed(1)} B`
                              } else if (revenueNumeric >= 1000000) {
                                formatted = `$ ${(revenueNumeric / 1000000).toFixed(1)} M`
                              } else if (revenueNumeric >= 1000) {
                                formatted = `$ ${(revenueNumeric / 1000).toFixed(1)} K`
                              } else {
                                formatted = `$ ${revenueNumeric.toFixed(1)}`
                              }
                            } else {
                              formatted = `$0.0`
                            }

                            return (
                              <div
                                className="stat-text"
                                {...(locked ? { role: 'button', tabIndex: 0 } : {})}
                                onClick={(e) => { if (locked) { e.stopPropagation(); openUpgradeModal && openUpgradeModal(); } }}
                                onKeyDown={(e) => { if (locked && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); e.stopPropagation(); openUpgradeModal && openUpgradeModal(); } }}
                              >
                                {locked ? (
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <span
                                      style={{
                                        filter: 'blur(4px)',
                                        WebkitFilter: 'blur(4px)',
                                        opacity: 0.6,
                                        userSelect: 'none',
                                        pointerEvents: 'none'
                                      }}
                                    >
                                      {formatted}
                                    </span>
                                    <i
                                      className="fas fa-lock"
                                      aria-hidden="true"
                                      style={{
                                        fontSize: 12,
                                        color: '#fff',
                                        cursor: 'pointer'
                                      }}
                                    />
                                  </span>
                                ) : (
                                  <div className="stat-text-inner">
                                    {formatted}
                                  </div>
                                )}
                              </div>
                            );
                          })()
                        }
                      </td>
                      <td>{formatDate(row.created_at || row.created_at || row.game?.created_at || row.game?.created_at)}</td>
                    </tr>
                  );
                } catch (error) {
                  console.error('Error rendering row:', error, row);
                  return (
                    <tr key={`error-${index}`}>
                      <td colSpan="10" className="error-row">
                        <span style={{ color: 'red' }}>Error rendering row data</span>
                      </td>
                    </tr>
                  );
                }
              })
            ) : (
              <tr>
                <td colSpan="10" style={{ textAlign: "center", color: COLORS.textMuted }}>
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        )}
      </table>
    </div>
  );
};

export default DataTable;
